'use client';

import React, { useState } from 'react';

interface CalendarProps {
  onDateSelect: (date: Date) => void;
  selectedDate?: Date | null;
  className?: string;
}

const Calendar: React.FC<CalendarProps> = ({
  onDateSelect,
  selectedDate: propSelectedDate,
  className = '',
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(propSelectedDate ?? null);

  const daysOfWeek = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const handleDateClick = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      onDateSelect(date);
    }
  };

  const isDateSelected = (date: Date | null) => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isDateAvailable = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return date >= new Date(today.setHours(0, 0, 0, 0)); // сегодня и позже
  };

  const days = getDaysInMonth(currentDate);

  const monthName = currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  return (
    <div data-component-id="src/components/ui/Calendar.tsx:71:4" data-component-path="src/components/ui/Calendar.tsx" data-component-line="71" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`bg-white rounded-lg ${className}`}>
      {/* Header with month and nav arrows */}
      <div data-component-id="src/components/ui/Calendar.tsx:73:6" data-component-path="src/components/ui/Calendar.tsx" data-component-line="73" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%20px-2%22%7D" className="flex items-center justify-between mb-4 px-2">
        <button data-component-id="src/components/ui/Calendar.tsx:74:8" data-component-path="src/components/ui/Calendar.tsx" data-component-line="74" data-component-file="Calendar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-gray-600%20hover%3Atext-black%20text-xl%20font-bold%22%2C%22textContent%22%3A%22%E2%80%B9%22%7D"
          onClick={goToPreviousMonth}
          className="text-gray-600 hover:text-black text-xl font-bold"
        >
          ‹
        </button>
        <span data-component-id="src/components/ui/Calendar.tsx:80:8" data-component-path="src/components/ui/Calendar.tsx" data-component-line="80" data-component-file="Calendar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-gray-800%20capitalize%22%7D" className="text-lg font-semibold text-gray-800 capitalize">{monthName}</span>
        <button data-component-id="src/components/ui/Calendar.tsx:81:8" data-component-path="src/components/ui/Calendar.tsx" data-component-line="81" data-component-file="Calendar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-gray-600%20hover%3Atext-black%20text-xl%20font-bold%22%2C%22textContent%22%3A%22%E2%80%BA%22%7D"
          onClick={goToNextMonth}
          className="text-gray-600 hover:text-black text-xl font-bold"
        >
          ›
        </button>
      </div>

      {/* Days of week */}
      <div data-component-id="src/components/ui/Calendar.tsx:90:6" data-component-path="src/components/ui/Calendar.tsx" data-component-line="90" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-1%20mb-4%22%7D" className="grid grid-cols-7 gap-1 mb-4">
        {daysOfWeek.map((day) => (
          <div data-component-id="src/components/ui/Calendar.tsx:92:10" data-component-path="src/components/ui/Calendar.tsx" data-component-line="92" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20text-sm%20font-medium%20text-%5B%23000000%5D%20py-2%22%7D" key={day} className="text-center text-sm font-medium text-[#000000] py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div data-component-id="src/components/ui/Calendar.tsx:99:6" data-component-path="src/components/ui/Calendar.tsx" data-component-line="99" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-4%22%7D" className="grid grid-cols-7 gap-4">
        {days.map((date, index) => (
          <div data-component-id="src/components/ui/Calendar.tsx:101:10" data-component-path="src/components/ui/Calendar.tsx" data-component-line="101" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22aspect-square%22%7D" key={index} className="aspect-square">
            {date ? (
              <button data-component-id="src/components/ui/Calendar.tsx:103:14" data-component-path="src/components/ui/Calendar.tsx" data-component-line="103" data-component-file="Calendar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => handleDateClick(date)}
                disabled={!isDateAvailable(date)}
                className={`w-10 lg:w-10 h-10 lg:h-10 rounded-full flex items-center justify-center text-sm lg:text-base font-medium transition-colors ${
                  isDateSelected(date)
                    ? 'bg-[#4d899e] text-white'
                    : isDateAvailable(date)
                      ? 'bg-[#4d899e] text-white hover:bg-[#3a7a8f]'
                      : 'text-[#21212166] cursor-not-allowed'
                }`}
              >
                {date.getDate()}
              </button>
            ) : (
              <div data-component-id="src/components/ui/Calendar.tsx:117:14" data-component-path="src/components/ui/Calendar.tsx" data-component-line="117" data-component-file="Calendar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%22%7D" className="w-10 h-10"></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;

// 'use client';

// import React, { useState } from 'react';

// interface CalendarProps {
//   onDateSelect: (date: Date) => void;
//   selectedDate?: Date | null;
//   className?: string;
// }

// const Calendar: React.FC<CalendarProps> = ({ onDateSelect, className = '' }) => {
//   const [currentDate, setCurrentDate] = useState(new Date());
//   const [selectedDate, setSelectedDate] = useState<Date | null>(null);

//   const daysOfWeek = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

//   const getDaysInMonth = (date: Date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth();
//     const firstDay = new Date(year, month, 1);
//     const lastDay = new Date(year, month + 1, 0);
//     const daysInMonth = lastDay.getDate();
//     const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Adjust for Monday start

//     const days = [];

//     // Add empty cells for days before the first day of the month
//     for (let i = 0; i < startingDayOfWeek; i++) {
//       days.push(null);
//     }

//     // Add days of the month
//     for (let day = 1; day <= daysInMonth; day++) {
//       days.push(new Date(year, month, day));
//     }

//     return days;
//   };

//   const handleDateClick = (date: Date | null) => {
//     if (date) {
//       setSelectedDate(date);
//       onDateSelect(date);
//     }
//   };

//   const isDateSelected = (date: Date | null) => {
//     if (!date || !selectedDate) return false;
//     return date.toDateString() === selectedDate.toDateString();
//   };

//   const isDateAvailable = (date: Date | null) => {
//     if (!date) return false;
//     const today = new Date();
//     return date >= today;
//   };

//   const days = getDaysInMonth(currentDate);

//   return (
//     <div className={`bg-white rounded-lg ${className}`}>
//       {/* Days of week header */}
//       <div className="grid grid-cols-7 gap-1 mb-4">
//         {daysOfWeek.map((day) => (
//           <div key={day} className="text-center text-sm font-medium text-[#000000] py-2">
//             {day}
//           </div>
//         ))}
//       </div>

//       {/* Calendar grid */}
//       <div className="grid grid-cols-7 gap-4">
//         {days.map((date, index) => (
//           <div key={index} className="aspect-square">
//             {date ? (
//               <button
//                 onClick={() => handleDateClick(date)}
//                 disabled={!isDateAvailable(date)}
//                 className={`w-10 lg:w-10 h-10 lg:h-10 rounded-full flex items-center justify-center text-sm lg:text-base font-medium transition-colors ${
//                   isDateSelected(date)
//                     ? 'bg-[#4d899e] text-white'
//                     : isDateAvailable(date)
//                       ? 'bg-[#4d899e] text-white hover:bg-[#3a7a8f]'
//                       : 'text-[#21212166] cursor-not-allowed'
//                 }`}
//               >
//                 {date.getDate()}
//               </button>
//             ) : (
//               <div className="w-10 h-10"></div>
//             )}
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Calendar;
